// THE PR DESK — every outstanding pull request across your repos in one place,
// each answering ONE question: *do your eyes need to be on this?*
//
// The founder's constraints (2026-07-29) are the design, not decoration:
//   * **Draft and open only, never closed.** The desk is for seeing what is
//     happening.
//   * **"Just because I can see them doesn't mean I need to be there to approve
//     them."** So the page LEADS with how many rows actually need him — ideally
//     zero — and everything else reads as information, not a queue of asks.
//     Nothing here is styled as a pending request unless it genuinely is one.
//   * **Review-in-progress is a first-class state**, distinct from "passed" and
//     from "nothing has looked yet".
//   * **A human asking for a human is a social contract** — such a row says so
//     explicitly, and says that Waytide will not approve or merge it.
//   * **Never rebuild GitHub's UI.** Every row is a link out. No diffs, no
//     comment threads, no file trees — GitHub already does that well.
//
// Partial data is stated, never hidden: a project we could not read is listed
// under the rows with its reason, because an unexplained empty desk is a lie.

import { useEffect, useState } from "react";
import { listPullRequestDesk } from "../../api/client";
import type { DeskRow, ReviewState, SkippedProject } from "@waytide/core";
import "./prdesk.css";

/** `updated_at` is GitHub's ISO-8601. Render it as calm relative time — the desk
 * is about what's live, so "3h ago" beats a timestamp. */
function fmtWhen(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/** The review state as a short human label. Deliberately distinguishes "nobody
 * has looked" from "looking now" from a score. */
function reviewLabel(review: ReviewState): string {
  switch (review.state) {
    case "in_progress":
      return `${review.reviewer} reviewing…`;
    case "scored":
      return `${review.reviewer} ${review.score}/5`;
    case "unreviewed":
    default:
      return "not reviewed";
  }
}

/** A stable modifier for styling the review pill by state. */
function reviewTone(review: ReviewState): string {
  switch (review.state) {
    case "in_progress":
      return "working";
    case "scored":
      return review.score >= 5 ? "clear" : "low";
    case "unreviewed":
    default:
      return "idle";
  }
}

const SKIP_REASON: Record<string, string> = {
  github_not_connected: "GitHub isn't connected to this project yet",
  repo_unreadable: "a bound repo couldn't be read (token or access changed)",
  upstream_error: "GitHub returned an error",
};

export function PrDesk() {
  const [rows, setRows] = useState<DeskRow[] | null>(null);
  const [skipped, setSkipped] = useState<SkippedProject[]>([]);
  const [needsYou, setNeedsYou] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const resp = await listPullRequestDesk();
        if (!alive) return;
        setRows(resp.pull_requests);
        setSkipped(resp.skipped);
        setNeedsYou(resp.needs_you_count);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section className="wt-prdesk">
      <header className="wt-prdesk__head">
        <h1 className="wt-prdesk__title">Pull requests</h1>
        {/* The lead line answers the only question that matters, before any list. */}
        {rows === null ? (
          <p className="wt-prdesk__lead">Reading your repos…</p>
        ) : needsYou === 0 ? (
          <p className="wt-prdesk__lead wt-prdesk__lead--calm">
            Nothing needs you.{" "}
            {rows.length > 0
              ? `${rows.length} open, all moving without you.`
              : "No open work right now."}
          </p>
        ) : (
          <p className="wt-prdesk__lead wt-prdesk__lead--attention">
            {needsYou} of {rows.length} could use your eyes.
          </p>
        )}
      </header>

      {error ? <p className="wt-prdesk__error">{error}</p> : null}

      {rows !== null && rows.length > 0 ? (
        <ul className="wt-prdesk__list">
          {rows.map((r) => (
            <li
              key={`${r.repo_full_name}#${r.number}`}
              className={
                r.needs_your_eyes
                  ? "wt-prdesk__row wt-prdesk__row--needs-you"
                  : "wt-prdesk__row"
              }
            >
              <div className="wt-prdesk__row-main">
                {/* The link out IS the interaction. We don't reproduce GitHub. */}
                <a
                  className="wt-prdesk__row-title"
                  href={r.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {r.title || `#${r.number}`}
                </a>
                <span className="wt-prdesk__row-where">
                  {r.repo_full_name} #{r.number} · {r.project_name}
                </span>
              </div>

              <div className="wt-prdesk__row-meta">
                {r.draft ? (
                  <span className="wt-prdesk__pill wt-prdesk__pill--draft">
                    draft
                  </span>
                ) : null}
                <span
                  className="wt-prdesk__pill"
                  data-tone={reviewTone(r.review)}
                >
                  {reviewLabel(r.review)}
                </span>
                {r.human_requested ? (
                  <span className="wt-prdesk__pill wt-prdesk__pill--human">
                    human asked for
                  </span>
                ) : null}
                <span className="wt-prdesk__when">{fmtWhen(r.updated_at)}</span>
              </div>

              {/* Always shown, including when the answer is no — the verdict is
                  never a black box. */}
              <p className="wt-prdesk__why">{r.why}</p>
            </li>
          ))}
        </ul>
      ) : null}

      {rows !== null && rows.length === 0 && !error ? (
        <p className="wt-prdesk__empty">
          No draft or open pull requests in your projects. Closed ones aren&apos;t
          shown here by design — this is for work in flight.
        </p>
      ) : null}

      {skipped.length > 0 ? (
        <div className="wt-prdesk__skipped">
          <h2 className="wt-prdesk__skipped-title">Not included</h2>
          <ul>
            {skipped.map((s) => (
              <li key={s.project_id}>
                <strong>{s.project_name}</strong> —{" "}
                {SKIP_REASON[s.reason] ?? s.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
